import React, { useCallback, useState } from 'react';
import { PlayingCard } from './PlayingCard';
type Suit = '♠' | '♥' | '♦' | '♣';
type Rank = '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A';
interface Card {
  id: string;
  rank: Rank;
  suit: Suit;
}
const rankOrder: Record<string, number> = {
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 7,
  '8': 8,
  '9': 9,
  '10': 10,
  J: 11,
  Q: 12,
  K: 13,
  A: 14
};
const rankChips: Record<string, number> = {
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 7,
  '8': 8,
  '9': 9,
  '10': 10,
  J: 10,
  Q: 10,
  K: 10,
  A: 11
};
const suits: Suit[] = ['♠', '♥', '♦', '♣'];
const ranks: Rank[] = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
function createDeck(): Card[] {
  const deck: Card[] = [];
  for (const suit of suits) {
    for (const rank of ranks) {
      deck.push({
        id: `${rank}${suit}`,
        rank,
        suit
      });
    }
  }
  return deck;
}
interface HandResult {
  name: string;
  english: string;
  baseChips: number;
  baseMult: number;
  scoringCards: string[];
  cardChips: number;
  totalChips: number;
  finalScore: number;
  emoji: string;
}
function detectHand(cards: Card[]): HandResult | null {
  if (cards.length === 0) return null;
  const sorted = [...cards].sort((a, b) => rankOrder[b.rank] - rankOrder[a.rank]);
  const rankCounts: Record<string, number> = {};
  const suitCounts: Record<string, number> = {};
  for (const card of sorted) {
    rankCounts[card.rank] = (rankCounts[card.rank] || 0) + 1;
    suitCounts[card.suit] = (suitCounts[card.suit] || 0) + 1;
  }
  const counts = Object.values(rankCounts).sort((a, b) => b - a);
  const isFlush = cards.length === 5 && Object.values(suitCounts).some(c => c === 5);
  const rankValues = sorted.map(c => rankOrder[c.rank]);
  const uniqueRanks = [...new Set(rankValues)].sort((a, b) => b - a);
  let isStraight = false;
  if (cards.length === 5 && uniqueRanks.length === 5) {
    const high = uniqueRanks[0];
    const low = uniqueRanks[4];
    if (high - low === 4) isStraight = true;
    // Ace-low straight: A-2-3-4-5
    if (uniqueRanks.join(',') === '14,5,4,3,2') isStraight = true;
  }
  let name = '',
    english = '',
    baseChips = 0,
    baseMult = 0,
    emoji = '';
  let scoringCards: string[] = [];
  if (cards.length === 5 && isFlush && isStraight) {
    const isRoyal = uniqueRanks.join(',') === '14,13,12,11,10';
    if (isRoyal) {
      name = '皇家同花顺';
      english = 'Royal Flush';
      baseChips = 100;
      baseMult = 8;
      emoji = '👑';
    } else {
      name = '同花顺';
      english = 'Straight Flush';
      baseChips = 100;
      baseMult = 8;
      emoji = '⚡';
    }
    scoringCards = sorted.map(c => c.id);
  } else if (counts[0] === 4) {
    name = '四条';
    english = 'Four of a Kind';
    baseChips = 60;
    baseMult = 7;
    emoji = '💪';
    const quadRank = Object.entries(rankCounts).find(([, v]) => v === 4)?.[0];
    scoringCards = sorted.filter(c => c.rank === quadRank).map(c => c.id);
  } else if (counts[0] === 3 && counts[1] === 2) {
    name = '葫芦';
    english = 'Full House';
    baseChips = 40;
    baseMult = 4;
    emoji = '🏠';
    scoringCards = sorted.map(c => c.id);
  } else if (cards.length === 5 && isFlush) {
    name = '同花';
    english = 'Flush';
    baseChips = 35;
    baseMult = 4;
    emoji = '❤️';
    scoringCards = sorted.map(c => c.id);
  } else if (cards.length === 5 && isStraight) {
    name = '顺子';
    english = 'Straight';
    baseChips = 30;
    baseMult = 4;
    emoji = '📈';
    scoringCards = sorted.map(c => c.id);
  } else if (counts[0] === 3) {
    name = '三条';
    english = 'Three of a Kind';
    baseChips = 30;
    baseMult = 3;
    emoji = '🎯';
    const tripleRank = Object.entries(rankCounts).find(([, v]) => v === 3)?.[0];
    scoringCards = sorted.filter(c => c.rank === tripleRank).map(c => c.id);
  } else if (counts[0] === 2 && counts[1] === 2) {
    name = '两对';
    english = 'Two Pair';
    baseChips = 20;
    baseMult = 2;
    emoji = '👥👥';
    const pairRanks = Object.entries(rankCounts).filter(([, v]) => v === 2).map(([r]) => r);
    scoringCards = sorted.filter(c => pairRanks.includes(c.rank)).map(c => c.id);
  } else if (counts[0] === 2) {
    name = '一对';
    english = 'One Pair';
    baseChips = 10;
    baseMult = 2;
    emoji = '👥';
    const pairRank = Object.entries(rankCounts).find(([, v]) => v === 2)?.[0];
    scoringCards = sorted.filter(c => c.rank === pairRank).map(c => c.id);
  } else {
    name = '高牌';
    english = 'High Card';
    baseChips = 5;
    baseMult = 1;
    emoji = '🃏';
    scoringCards = [sorted[0].id];
  }
  const cardChips = scoringCards.reduce((sum, id) => {
    const card = cards.find(c => c.id === id);
    return sum + (card ? rankChips[card.rank] : 0);
  }, 0);
  const totalChips = baseChips + cardChips;
  const finalScore = totalChips * baseMult;
  return {
    name,
    english,
    baseChips,
    baseMult,
    scoringCards,
    cardChips,
    totalChips,
    finalScore,
    emoji
  };
}
const DECK = createDeck();
export function InteractiveHandBuilder() {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showHint, setShowHint] = useState(true);
  const toggleCard = useCallback((card: Card) => {
    setSelectedIds(prev => {
      if (prev.includes(card.id)) {
        return prev.filter(id => id !== card.id);
      }
      if (prev.length >= 5) return prev;
      return [...prev, card.id];
    });
    setShowHint(false);
  }, []);
  const clearAll = () => {
    setSelectedIds([]);
    setShowHint(true);
  };
  const selectedCards = selectedIds.map(id => DECK.find(c => c.id === id)!).filter(Boolean);
  const result = selectedCards.length > 0 ? detectHand(selectedCards) : null;
  return <div className="space-y-6" data-id="element-123">
      <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-124">
        <div data-id="element-125">
          <h3 className="text-lg font-bold" style={{
          color: '#FFD700',
          fontFamily: 'Cinzel, serif'
        }} data-id="element-126">
            🎮 互动出牌练习台
          </h3>
          <p className="text-sm mt-1" style={{
          color: '#b090d0'
        }} data-id="element-127">
            从下方牌组中选择最多5张牌，系统自动识别牌型并计算得分
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-128">
          <span className="text-sm number-mono" style={{
          color: '#9080b0'
        }} data-id="element-129">
            已选 {selectedIds.length}/5 张
          </span>
          <button onClick={clearAll} className="px-3 py-1.5 rounded-lg text-sm transition-all duration-200" style={{
          background: 'rgba(255,68,68,0.1)',
          border: '1px solid rgba(255,68,68,0.3)',
          color: '#ff6666',
          cursor: 'pointer'
        }} data-id="element-130">
            清空
          </button>
        </div>
      </div>

      {/* Selected hand display */}
      <div className="panel p-4 min-h-32 flex flex-col items-center justify-center" style={{
      minHeight: 140
    }} data-id="element-131">
        {selectedCards.length === 0 ? <div className="text-center" data-id="element-132">
            <div className="text-4xl mb-2" data-id="element-133">👆</div>
            <p className="text-sm" style={{
          color: '#7060a0'
        }} data-id="element-134">
              从下方选择牌来组成你的手牌
            </p>
          </div> : <div className="w-full" data-id="element-135">
            <div className="flex gap-3 justify-center mb-4 flex-wrap" data-id="element-136">
              {selectedCards.map(card => <div key={card.id} onClick={() => toggleCard(card)} data-id="element-137">
                  <PlayingCard rank={card.rank} suit={card.suit} size="lg" selected highlighted={result?.scoringCards.includes(card.id)} data-id="element-138" />
                </div>)}
              {/* Empty slots */}
              {Array.from({
            length: 5 - selectedCards.length
          }).map((_, i) => <div key={`empty-${i}`} style={{
            width: 90,
            height: 128,
            borderRadius: 8,
            border: '2px dashed rgba(255,215,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-139">
                  <span style={{
              color: 'rgba(255,215,0,0.2)',
              fontSize: 24
            }} data-id="element-140">
                    +
                  </span>
                </div>)}
            </div>

            {/* Result */}
            {result && <div className="animate-score-pop" data-id="element-141">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-142">
                  <span className="text-2xl" data-id="element-143">{result.emoji}</span>
                  <span className="text-xl font-bold" style={{
              color: '#FFD700',
              fontFamily: 'Cinzel, serif'
            }} data-id="element-144">
                    {result.name}
                  </span>
                  <span className="text-sm" style={{
              color: '#9080b0'
            }} data-id="element-145">
                    ({result.english})
                  </span>
                </div>

                <div className="flex items-center justify-center gap-2 flex-wrap" data-id="element-146">
                  <div className="text-center" data-id="element-147">
                    <div className="text-xs mb-1" style={{
                color: '#9080b0'
              }} data-id="element-148">
                      基础筹码
                    </div>
                    <div className="chip-badge px-2 py-1 rounded number-mono" data-id="element-149">
                      {result.baseChips}
                    </div>
                  </div>
                  <span style={{
              color: '#7060a0'
            }} data-id="element-150">
                    +
                  </span>
                  <div className="text-center" data-id="element-151">
                    <div className="text-xs mb-1" style={{
                color: '#9080b0'
              }} data-id="element-152">
                      牌面筹码
                    </div>
                    <div className="chip-badge px-2 py-1 rounded number-mono" data-id="element-153">
                      {result.cardChips}
                    </div>
                  </div>
                  <span style={{
              color: '#7060a0'
            }} data-id="element-154">
                    =
                  </span>
                  <div className="text-center" data-id="element-155">
                    <div className="text-xs mb-1" style={{
                color: '#9080b0'
              }} data-id="element-156">
                      总筹码
                    </div>
                    <div className="chip-badge px-2 py-1 rounded number-mono" data-id="element-157">
                      {result.totalChips}
                    </div>
                  </div>
                  <span style={{
              color: '#7060a0'
            }} data-id="element-158">
                    ×
                  </span>
                  <div className="text-center" data-id="element-159">
                    <div className="text-xs mb-1" style={{
                color: '#9080b0'
              }} data-id="element-160">
                      倍率
                    </div>
                    <div className="mult-badge px-2 py-1 rounded number-mono" data-id="element-161">
                      {result.baseMult}
                    </div>
                  </div>
                  <span style={{
              color: '#7060a0'
            }} data-id="element-162">
                    =
                  </span>
                  <div className="text-center" data-id="element-163">
                    <div className="text-xs mb-1" style={{
                color: '#9080b0'
              }} data-id="element-164">
                      得分
                    </div>
                    <div className="score-badge px-3 py-1 rounded-lg text-lg number-mono" data-id="element-165">
                      {result.finalScore}
                    </div>
                  </div>
                </div>

                {result.scoringCards.length < selectedCards.length && <p className="text-xs text-center mt-2" style={{
            color: '#9080b0'
          }} data-id="element-166">
                    💡 高亮的牌参与计分，其余牌不计入筹码
                  </p>}
              </div>}
          </div>}
      </div>

      {/* Deck */}
      <div data-id="element-167">
        <p className="text-xs mb-3" style={{
        color: '#9080b0'
      }} data-id="element-168">
          点击选牌（最多5张）：
        </p>
        <div className="space-y-3" data-id="element-169">
          {suits.map(suit => <div key={suit} className="flex items-center gap-1 flex-wrap" data-id="element-170">
              <span className="text-xl w-7 flex-shrink-0 text-center" style={{
            color: suit === '♥' || suit === '♦' ? '#cc2222' : '#e8d5ff'
          }} data-id="element-171">
                {suit}
              </span>
              <div className="flex gap-1 flex-wrap" data-id="element-172">
                {ranks.map(rank => {
              const card = DECK.find(c => c.rank === rank && c.suit === suit)!;
              const isSelected = selectedIds.includes(card.id);
              const isDisabled = !isSelected && selectedIds.length >= 5;
              return <div key={card.id} style={{
                opacity: isDisabled ? 0.4 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer'
              }} data-id="element-173">
                      <PlayingCard rank={rank} suit={suit} size="xs" selected={isSelected} onClick={isDisabled ? undefined : () => toggleCard(card)} disabled={isDisabled} data-id="element-174" />
                    </div>;
            })}
              </div>
            </div>)}
        </div>
      </div>

      {/* Quick presets */}
      <div data-id="element-175">
        <p className="text-xs mb-3" style={{
        color: '#9080b0'
      }} data-id="element-176">
          快速示例：
        </p>
        <div className="flex flex-wrap gap-2" data-id="element-177">
          {[{
          label: '一对A',
          cards: ['A♠', 'A♥', 'K♦', '9♣', '5♠']
        }, {
          label: '顺子',
          cards: ['9♠', '8♥', '7♦', '6♣', '5♠']
        }, {
          label: '同花',
          cards: ['A♥', 'J♥', '8♥', '5♥', '2♥']
        }, {
          label: '葫芦',
          cards: ['K♠', 'K♥', 'K♦', 'Q♣', 'Q♥']
        }, {
          label: '四条',
          cards: ['A♠', 'A♥', 'A♦', 'A♣', 'K♠']
        }, {
          label: '同花顺',
          cards: ['9♣', '8♣', '7♣', '6♣', '5♣']
        }].map(preset => <button key={preset.label} onClick={() => {
          const ids = preset.cards.map(c => {
            const rank = c.slice(0, -1) as Rank;
            const suit = c.slice(-1) as Suit;
            return `${rank}${suit}`;
          });
          setSelectedIds(ids);
          setShowHint(false);
        }} className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200" style={{
          background: 'rgba(255,215,0,0.08)',
          border: '1px solid rgba(255,215,0,0.25)',
          color: '#c0a040',
          cursor: 'pointer'
        }} data-id="element-178">
              {preset.label}
            </button>)}
        </div>
      </div>
    </div>;
}